<script setup lang="ts">
import type { AxiosError } from 'axios';
import type { PostSubmit, PreSubmit } from '~/lib/fw/types';
import {
  CloudUploadOutlined,
  SaveOutlined,
  UserSwitchOutlined,
} from '@ant-design/icons-vue';
import { notification } from 'ant-design-vue';
import { cloneDeep } from 'lodash-es';
import { onUnmounted, reactive, ref, toRaw } from 'vue';
import { useConfig } from '~/lib/fw/useFw';
import { poll, post, toFormValues } from '~/lib/fw/utils';

const props = withDefaults(defineProps<Props>(), {
  initialModel: () => ({}),
  submitButtonText: 'Salvar',
  showSubmitSucessResult: true,
  showSubmitErrorNotification: true,
  submitButtonIconVisible: true,
  waitForResult: true,
  showUploadFiles: true,
  devolverButtonVisible: false,
});

const emit = defineEmits(['success', 'error', 'close']);

const fwConfig = useConfig();

interface Props {
  action: string;
  pastaCodigos?: string[];
  publicacaoNumeroProcessoCodigos?: string[];
  compromissoCodigos?: string[];
  initialModel?: object;
  preSubmit?: PreSubmit;
  postSubmit?: PostSubmit;
  submitButtonDisabled?: boolean;
  submitButtonBlock?: boolean;
  submitButtonText?: string;
  submitButtonIconVisible?: boolean;
  submitErrorNotificationVisible?: boolean;
  waitForResult?: boolean;
  showUploadFiles?: boolean;
  devolverButtonVisible?: boolean;
  devolverButtonDisabled?: boolean;
  showSubmitSucessResult?: boolean;
  showSubmitErrorNotification?: boolean;
}

const uploaderStatus = ref<any>(null);
const uploaderRef = ref<any>(null);
const jobId = ref<string | undefined>(undefined);
// eslint-disable-next-line ts/no-unsafe-function-type
const cancelPollJobFunction = ref<Function | undefined>(undefined);
const commandData = ref(null);
const formRef = ref<any>();
const submitStatus = ref<'loading' | 'success' | 'error' | null>(null);
const originalModel: any = cloneDeep(toRaw(props.initialModel));
const model = reactive<any>(props.initialModel ?? {});
let resultModel: any = {};
const erroInesperadoMessage = 'Tivemos um problema inesperado';
const devolverModalVisible = ref(false);
const justificativaDevolucao = ref<any>(null);
const devolverStatus = ref<'loading' | 'success' | 'error' | null>(null);

function getErrorMessage(error: AxiosError): string {
  if (error.status === 400) {
    return (
      (error.response?.data as any)?.errorMessage ??
      'Tivemos um problema inesperado'
    );
  } else {
    return 'Tivemos um problema inesperado';
  }
}

async function submit() {
  await formRef.value.validate();

  submitStatus.value = 'loading';

  resultModel = cloneDeep(toRaw(model));

  let requestData: any = toFormValues(resultModel, originalModel);

  if (props.showUploadFiles) {
    requestData.arquivos = undefined;
    await uploaderRef.value.upload();
  }

  if (props.preSubmit) {
    requestData = await props.preSubmit(requestData);

    if (requestData === null) {
      submitStatus.value = null;
    }
  }

  interface SubmitJobResult {
    jobId: string;
  }

  const submitJobResult = await post<SubmitJobResult>(
    `${fwConfig.apiUrl}/${props.action}`,
    {
      pastaCodigos: props.pastaCodigos,
      publicacaoNumeroProcessoCodigos: props.publicacaoNumeroProcessoCodigos,
      compromissoCodigos: props.compromissoCodigos,
      arquivos: props.showUploadFiles ? resultModel.arquivos : undefined,
      command: requestData,
    },
  );

  if (!submitJobResult.ok) {
    if (props.showSubmitErrorNotification) {
      const errorMessage = getErrorMessage(submitJobResult.error as AxiosError);

      notification.error({
        message: errorMessage,
        duration: 10,
      });
    }

    submitStatus.value = 'error';

    return;
  }

  jobId.value = submitJobResult.value.jobId;

  if (props.waitForResult && jobId.value) {
    try {
      const pollJobResult = poll(`${fwConfig.apiUrl}/jobs/${jobId.value}`, {
        delay: 3000,
        maxAttempts: 20,
      });
      cancelPollJobFunction.value = pollJobResult.cancel;
      const response = await pollJobResult.promise;

      commandData.value = response;

      if (response.status === 4) {
        if (props.showSubmitErrorNotification) {
          notification.error({
            message: erroInesperadoMessage,
            duration: 10,
          });
        }
        submitStatus.value = 'error';
        return;
      }

      const results = response.results;

      if (results?.length === 1 && !results[0].isSuccess) {
        const jobData = results[0];

        if (props.showSubmitErrorNotification) {
          const errorMessage = jobData.errors[0];

          notification.error({
            message: errorMessage,
            duration: 10,
          });
        }

        submitStatus.value = 'error';
        return;
      }

      submitStatus.value = 'success';
    } catch {
      if (props.showSubmitErrorNotification) {
        notification.error({
          message: erroInesperadoMessage,
          duration: 10,
        });
      }

      submitStatus.value = 'error';
    }
  } else {
    submitStatus.value = 'success';
  }
}

async function devolver() {
  devolverStatus.value = 'loading';

  const devolverResult = await post<any>(
    `${fwConfig.apiUrl}/compromissos/devolver-compromisso`,
    {
      codigoCompromisso: props.compromissoCodigos![0],
      justificativaDevolucao: justificativaDevolucao.value,
    },
  );

  if (!devolverResult.ok) {
    notification.error({
      message: 'Tivemos um problema ao tentar devolver o compromisso',
      duration: 5,
    });

    devolverStatus.value = 'error';
    return;
  }

  devolverModalVisible.value = false;
  devolverStatus.value = 'success';
}

onUnmounted(() => {
  if (cancelPollJobFunction.value) {
    cancelPollJobFunction.value();
  }
});

defineExpose({
  model,
});
</script>

<template>
  <a-form
    v-if="
      !props.showSubmitSucessResult ||
      (submitStatus !== 'success' && devolverStatus !== 'success')
    "
    ref="formRef"
    :model="model"
    name="basic"
    autocomplete="off"
    layout="vertical"
  >
    <a-alert
      v-if="
        (props.pastaCodigos && props.pastaCodigos?.length > 1) ||
        (props.publicacaoNumeroProcessoCodigos &&
          props.publicacaoNumeroProcessoCodigos.length > 1)
      "
      type="warning"
      message="Você irá realizar uma alteração em lote"
      show-icon
      style="margin-bottom: 24px"
    />

    <slot name="form" :model="model" />

    <Uploader
      v-if="props.showUploadFiles"
      ref="uploaderRef"
      v-model:file-list="model.arquivos"
      style="margin-top: 24px; margin-bottom: 24px"
      @start="() => (uploaderStatus = 'uploading')"
      @end="() => (uploaderStatus = 'done')"
    >
      <template #description>
        <p class="ant-upload-drag-icon">
          <CloudUploadOutlined />
        </p>
        <p class="ant-upload-text">Clique aqui ou arraste os arquivos</p>
        <p class="ant-upload-hint">Envie os arquivos necessários</p>
      </template>
    </Uploader>

    <a-form-item>
      <a-button
        :block="props.submitButtonBlock"
        type="primary"
        :loading="submitStatus === 'loading'"
        size="large"
        :disabled="props.submitButtonDisabled"
        @click.prevent="submit"
      >
        <template v-if="props.submitButtonIconVisible" #icon>
          <slot name="submitButtonIcon">
            <SaveOutlined />
          </slot>
        </template>
        {{ submitButtonText }}
      </a-button>
      <a-button
        v-if="props.devolverButtonVisible"
        :disabled="props.devolverButtonDisabled"
        size="large"
        style="margin-left: 8px"
        @click="() => (devolverModalVisible = true)"
      >
        <template #icon>
          <UserSwitchOutlined />
        </template>
        Devolver
      </a-button>
    </a-form-item>
  </a-form>

  <a-modal
    v-model:visible="devolverModalVisible"
    title="Justifique a devolução"
    cancel-text="Cancelar"
    :z-index="2000"
    :confirm-loading="devolverStatus === 'loading'"
    :ok-button-props="{
      disabled: !justificativaDevolucao?.length,
    }"
    @ok="devolver"
  >
    <a-textarea
      v-model:value="justificativaDevolucao"
      :rows="4"
      placeholder="Escreva sua justificativa"
    />
  </a-modal>

  <a-result
    v-if="devolverStatus === 'success'"
    status="success"
    title="Compromisso devolvido com sucesso"
    sub-title="Agora você pode fechar essa janela"
  >
    <template #extra>
      <a-button @click="() => emit('close')"> Fechar </a-button>
    </template>
  </a-result>

  <slot
    v-if="props.showSubmitSucessResult && submitStatus === 'success'"
    name="submitSuccess"
    :data="commandData"
  >
    <a-result
      status="success"
      title="Registro realizado com sucesso"
      sub-title="Agora você pode fechar essa janela"
    >
      <template #extra>
        <a-button @click="() => emit('close')"> Fechar </a-button>
      </template>
    </a-result>
  </slot>
</template>
